import { useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight } from "lucide-react";
import { Modal } from "../Modal";
import { Field } from "../FormLayout";
import { ExportButtons } from "../ExportButtons";
import StudentSearchField from "../StudentSearchField";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { humanise } from "../register/registerLogic";
import { formatSchoolDate } from "../students/studentDisplay";
import BankReconciliationStatementView from "./BankReconciliationStatementView";
import { academicSetupService } from "../../services/academicSetupService";
import {
  financialService,
  type AgeingDetailRow,
  type AgeingReport,
  type AuditApprovalEvent,
  type StudentOption,
} from "../../services/financialService";
import { apiErrorMessage } from "../../utils/apiError";
import { todayIso } from "../../utils/dates";
import {
  AGEING_BUCKETS,
  ageingBucket,
  assetStatusDot,
  auditEventConfig,
  classShares,
  controlStudentConfig,
  controlSupplierConfig,
  documentName,
  fixedAssetConfig,
  grantSummaryConfig,
  isReportView,
  payablesAgeingConfig,
  receivablesAgeingConfig,
  REPORT_VIEWS,
  studentsOwingConfig,
  type ControlStudent,
  type ControlSupplier,
  type FixedAssetRow,
  type GrantSummaryRow,
  type ReportView,
  type StudentOwing,
} from "./managementReports";
import { quickDays, quickPeriods } from "./reportPeriods";
import { AsAtBar, PeriodBar } from "./ReportDateBar";

const money = (value: string | number | null | undefined) => Number(value || 0).toLocaleString();
const ugx = (value: string | number | null | undefined) => `UGX ${money(value)}`;
const day = (value: string | null | undefined) => formatSchoolDate(value) || "—";
const range = (start: string, end: string) => `${day(start)} – ${day(end)}`;

type Download = (format: "csv" | "pdf") => void;
export type Party = { id: number; name: string };

const VIEW_HINTS: Record<ReportView, string> = {
  income: "What the school earned and spent over a period, where it stands at the end of it, and the cash that moved.",
  arrears: "Who owes fees, by class and by learner, as things stand today.",
  ageing: "How long unpaid invoices and supplier bills have been outstanding on a given day.",
  reconciliations: "Proof that the student, supplier and bank records agree with the general ledger.",
  grants: "Donor grants and what is left of each, and the capitalised assets the school holds.",
  audit: "Every submission, approval, rejection and reversal on finance documents over a period.",
};

// Building blocks --------------------------------------------------------------------

export function ReportCard({
  title,
  description,
  aside,
  tone,
  className = "",
  children,
}: {
  title: string;
  description?: ReactNode;
  aside?: ReactNode;
  tone?: "danger";
  className?: string;
  children: ReactNode;
}) {
  return (
    <section className={`card ${tone === "danger" ? "border border-red-300" : ""} ${className}`}>
      <div className="card-titlebar">
        <div className="min-w-0">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
        </div>
        {aside && <div className="flex flex-wrap items-center gap-3">{aside}</div>}
      </div>
      <div className="px-6 py-4">{children}</div>
    </section>
  );
}

/** The CSV and PDF the server issues for a report, with the school's heading. */
export function ServerExport({ show, what, onExport }: { show: boolean; what: string; onExport: Download }) {
  if (!show) return null;
  return <ExportButtons formats={["csv", "pdf"]} what={what} onExport={(format) => onExport(format as "csv" | "pdf")} />;
}

/** Label-and-amount lines; the last can be a ruled total. */
function Figures({ rows, loading }: { rows: [string, ReactNode, "total"?][]; loading?: boolean }) {
  return (
    <dl className="divide-y divide-gray-100 text-sm">
      {rows.map(([label, value, kind]) => (
        <div key={label} className={`flex justify-between gap-4 py-2 ${kind === "total" ? "border-t border-gray-300 font-semibold text-gray-900" : ""}`}>
          <dt className={kind === "total" ? "" : "text-gray-600"}>{label}</dt>
          <dd className="tabular-nums">{loading ? <span className="inline-block h-4 w-24 animate-pulse rounded bg-gray-100" /> : value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Tile({ label, value, tone = "text-gray-900", dot }: { label: string; value: ReactNode; tone?: string; dot?: string }) {
  return (
    <div className="rounded-lg bg-gray-50 px-3 py-2">
      <dt className="flex items-center gap-1.5 text-xs text-gray-500">
        {dot && <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />}
        {label}
      </dt>
      <dd className={`mt-0.5 font-semibold tabular-nums ${tone}`}>{value}</dd>
    </div>
  );
}

interface SheetColumn<T> {
  header: string;
  cell: (row: T) => ReactNode;
  align?: "right";
}

/** A short report table that needs no paging: a handful of classes or fee types. */
export function SheetTable<T>({
  columns,
  rows,
  rowKey,
  footer,
  empty,
  onRow,
  rowTitle,
}: {
  columns: SheetColumn<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string | number;
  footer?: ReactNode[];
  empty: string;
  onRow?: (row: T) => void;
  rowTitle?: string;
}) {
  if (!rows) return <div className="h-24 animate-pulse rounded bg-gray-100" />;
  if (!rows.length) return <p className="rounded-lg border border-dashed border-gray-300 py-6 text-center text-sm text-gray-500">{empty}</p>;
  return (
    <div className="sheet-wrap">
      <table className="sheet">
        <thead>
          <tr>{columns.map((column) => <th key={column.header} className={column.align === "right" ? "text-right" : undefined}>{column.header}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={rowKey(row)} className={onRow ? "cursor-pointer" : undefined} onClick={onRow && (() => onRow(row))} title={rowTitle}>
              {columns.map((column) => (
                <td key={column.header} className={column.align === "right" ? "text-right tabular-nums" : undefined}>{column.cell(row)}</td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer && (
          <tfoot>
            <tr className="bg-gray-100 font-semibold text-gray-900">
              {footer.map((cell, index) => <td key={index} className={columns[index]?.align === "right" ? "text-right tabular-nums" : undefined}>{cell}</td>)}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

// Income & cash ----------------------------------------------------------------------

function IncomeAndCash({ start, end, canExport }: { start: string; end: string; canExport: boolean }) {
  const accounting = useQuery({
    queryKey: ["accounting-report", start, end],
    queryFn: () => financialService.getAccountingReport({ start_date: start, end_date: end }),
  });
  const stats = useQuery({ queryKey: ["financial-stats"], queryFn: financialService.getStats });
  const feeCollection = useQuery({
    queryKey: ["fee-collection-report", start, end],
    queryFn: () => financialService.getFeeCollectionReport({ start_date: start, end_date: end }),
  });
  const cashFlow = useQuery({
    queryKey: ["cash-flow-report", start, end],
    queryFn: () => financialService.getCashFlowReport({ start_date: start, end_date: end }),
  });
  const income = accounting.data?.income_statement;
  const position = accounting.data?.balance_sheet;
  const period = { start_date: start, end_date: end };
  const collected = Number(feeCollection.data?.total_collected ?? 0);
  const surplus = Number(income?.surplus ?? 0);
  const net = Number(cashFlow.data?.net_cash_movement ?? 0);

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-3">
        <ReportCard
          title="Income statement"
          description={range(start, end)}
          aside={<ServerExport show={canExport} what="the income statement" onExport={(format) => financialService.downloadAccountingReport("income_statement", format, period)} />}
        >
          <Figures
            loading={accounting.isLoading}
            rows={[
              ["Income", ugx(income?.income)],
              ["Expenses", ugx(income?.expenses)],
              [surplus < 0 ? "Deficit" : "Surplus", <span className={surplus < 0 ? "text-red-700" : undefined}>{ugx(Math.abs(surplus))}</span>, "total"],
            ]}
          />
        </ReportCard>
        <ReportCard
          title="Balance sheet"
          description={`As at ${day(end)}, from the first entry`}
          aside={<ServerExport show={canExport} what="the balance sheet" onExport={(format) => financialService.downloadAccountingReport("balance_sheet", format, period)} />}
        >
          <Figures
            loading={accounting.isLoading}
            rows={[
              ["Assets", ugx(position?.assets)],
              ["Liabilities", ugx(position?.liabilities)],
              ["Equity and surplus to date", ugx(Number(position?.equity ?? 0) + Number(position?.current_surplus ?? 0)), "total"],
            ]}
          />
        </ReportCard>
        <ReportCard title="Receivables" description="Every fee invoice raised so far">
          <Figures
            loading={stats.isLoading}
            rows={[
              ["Total billed", ugx(stats.data?.total_billed)],
              ["Collection rate", `${Number(stats.data?.collection_rate ?? 0).toFixed(1)}%`],
              ["Still outstanding", ugx(stats.data?.outstanding_balance), "total"],
            ]}
          />
        </ReportCard>
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <ReportCard
          title="Fee collection"
          description={`${ugx(collected)} from ${feeCollection.data?.payment_count ?? 0} payments, ${range(start, end)}`}
          aside={<ServerExport show={canExport} what="the fee collection report" onExport={(format) => financialService.downloadFeeCollectionReport(format, period)} />}
        >
          <SheetTable
            rows={feeCollection.data?.by_fee_type}
            rowKey={(row) => row.fee_type}
            empty="No fees were collected in this period."
            columns={[
              { header: "Fee", cell: (row) => <span className="font-medium text-gray-900">{row.fee_type}</span> },
              { header: "Collected (UGX)", cell: (row) => money(row.amount), align: "right" },
              { header: "Share", cell: (row) => `${collected ? ((Number(row.amount) / collected) * 100).toFixed(1) : "0.0"}%`, align: "right" },
            ]}
            footer={["Total", money(collected), "100%"]}
          />
        </ReportCard>

        <ReportCard
          title="Cash flow"
          description={range(start, end)}
          aside={<ServerExport show={canExport} what="the cash flow report" onExport={(format) => financialService.downloadCashFlowReport(format, period)} />}
        >
          <div className="space-y-4">
            <div className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm font-semibold ${net < 0 ? "bg-red-50 text-red-800" : "bg-green-50 text-green-800"}`}>
              <span>Net cash {net < 0 ? "outflow" : "inflow"}</span>
              <span className="tabular-nums">{ugx(Math.abs(net))}</span>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <SheetTable
                rows={cashFlow.data?.receipts}
                rowKey={(row) => row.source}
                empty="No money came in."
                columns={[
                  { header: "Money in", cell: (row) => row.source },
                  { header: "UGX", cell: (row) => money(row.amount), align: "right" },
                ]}
                footer={["Total in", money(cashFlow.data?.total_receipts)]}
              />
              <SheetTable
                rows={cashFlow.data?.payments}
                rowKey={(row) => row.source}
                empty="No money went out."
                columns={[
                  { header: "Money out", cell: (row) => row.source },
                  { header: "UGX", cell: (row) => money(row.amount), align: "right" },
                ]}
                footer={["Total out", money(cashFlow.data?.total_payments)]}
              />
            </div>
          </div>
        </ReportCard>
      </div>
    </div>
  );
}

// Arrears ----------------------------------------------------------------------------

export const studentsOwingColumns: RegisterColumn<StudentOwing>[] = [
  { header: "Student", cell: (row) => <span className="font-medium text-gray-900">{row.student_name}</span>, exportValue: (row) => row.student_name },
  { header: "Number", cell: (row) => row.student_code, exportValue: (row) => row.student_code, className: "tabular-nums text-gray-600" },
  { header: "Class", cell: (row) => row.class_name, exportValue: (row) => row.class_name },
  { header: "Unpaid invoices", cell: (row) => row.invoice_count, exportValue: (row) => row.invoice_count, align: "right" },
  { header: "Owed (UGX)", cell: (row) => <span className="font-semibold text-red-700">{money(row.balance)}</span>, exportValue: (row) => Number(row.balance), align: "right" },
];

function Arrears({ canExport, onOpenStudent }: { canExport: boolean; onOpenStudent: (student: StudentOption) => void }) {
  const outstanding = useQuery({ queryKey: ["outstanding-fees-report"], queryFn: financialService.getOutstandingFeesReport });
  const [classFilter, setClassFilter] = useState("");
  const shares = outstanding.data ? classShares(outstanding.data.by_class) : undefined;
  const showClass = (className: string) => {
    setClassFilter(className);
    document.getElementById("students-owing")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="space-y-4">
      <div className="grid gap-4 lg:grid-cols-3">
        <ReportCard
          className="lg:col-span-2"
          title="Outstanding fees by class"
          description={`${ugx(outstanding.data?.total)} owed across ${shares?.length ?? 0} classes. Click a class to list its learners.`}
          aside={<ServerExport show={canExport} what="the outstanding fees report, invoice by invoice" onExport={(format) => financialService.downloadOutstandingFeesReport(format)} />}
        >
          <SheetTable
            rows={shares}
            rowKey={(row) => row.class_name}
            empty="No class has unpaid fees."
            onRow={(row) => showClass(row.class_name)}
            rowTitle="List this class's learners"
            columns={[
              { header: "Class", cell: (row) => <span className="font-medium text-gray-900">{row.class_name}</span> },
              {
                header: "Share of arrears",
                cell: (row) => (
                  <div className="flex min-w-32 items-center gap-2">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-gray-100">
                      <div className="h-full rounded-full bg-red-400" style={{ width: `${row.share}%` }} />
                    </div>
                    <span className="w-12 text-right text-xs tabular-nums text-gray-500">{row.share.toFixed(1)}%</span>
                  </div>
                ),
              },
              { header: "Owed (UGX)", cell: (row) => money(row.amount), align: "right" },
            ]}
            footer={["Total", "", money(outstanding.data?.total)]}
          />
        </ReportCard>
        <ReportCard title="Look up a student" description="Open any learner's account, whether they owe or not.">
          <Field as="div" label="Student" hint="Search by name or student number.">
            <StudentSearchField name="report-student" required={false} onSelect={(student) => student && onOpenStudent(student)} />
          </Field>
        </ReportCard>
      </div>

      <div id="students-owing" className="scroll-mt-4">
        <ClientRegister
          key={classFilter}
          initialState={{ category: classFilter }}
          title="Students with outstanding fees"
          description="Every learner with an unpaid balance. Click a row to open the account and statement."
          rows={outstanding.data?.by_student}
          config={studentsOwingConfig}
          columns={studentsOwingColumns}
          rowKey={(row) => row.student_pk}
          onOpen={(row) => onOpenStudent({ id: row.student_pk, full_name: row.student_name, student_id: row.student_code, class_name: row.class_name })}
          openLabel={() => "Account"}
          noun={["student", "students"]}
          exportName={{ file: "students-with-outstanding-fees", title: "Students with outstanding fees" }}
          isLoading={outstanding.isLoading}
          isError={outstanding.isError}
          onRetry={() => void outstanding.refetch()}
          emptyText="No student owes fees."
          canExport={canExport}
        />
      </div>
    </div>
  );
}

// Ageing -----------------------------------------------------------------------------

export function AgeingSummary({ title, description, data, loading, aside }: { title: string; description: string; data?: AgeingReport; loading: boolean; aside: ReactNode }) {
  const total = Number(data?.total ?? 0);
  return (
    <ReportCard title={title} description={description} aside={aside}>
      {loading || !data ? (
        <div className="h-20 animate-pulse rounded bg-gray-100" />
      ) : (
        <div className="space-y-3">
          <div className="flex h-2 overflow-hidden rounded-full bg-gray-100" aria-hidden>
            {AGEING_BUCKETS.map((bucket) => (
              <div key={bucket.key} className={bucket.dot} style={{ width: `${total ? (Number(data.buckets[bucket.key]) / total) * 100 : 0}%` }} />
            ))}
          </div>
          <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            {AGEING_BUCKETS.map((bucket) => <Tile key={bucket.key} label={bucket.label} dot={bucket.dot} value={money(data.buckets[bucket.key])} />)}
            <Tile label="Total (UGX)" value={money(data.total)} tone="text-primary-700" />
          </dl>
        </div>
      )}
    </ReportCard>
  );
}

const overdueBy = (row: AgeingDetailRow) => ageingBucket(row.bucket);

export const ageingColumns = (party: string, reference: string): RegisterColumn<AgeingDetailRow>[] => [
  { header: party, cell: (row) => <span className="font-medium text-gray-900">{row.student_name}</span>, exportValue: (row) => row.student_name },
  ...(party === "Student"
    ? [{ header: "Class", cell: (row: AgeingDetailRow) => row.class_name || "—", exportValue: (row: AgeingDetailRow) => row.class_name ?? "" }]
    : []),
  { header: reference, cell: (row) => row.invoice_number, exportValue: (row) => row.invoice_number, className: "tabular-nums text-gray-600" },
  { header: "Due", cell: (row) => day(row.due_date), exportValue: (row) => row.due_date, className: "tabular-nums" },
  { header: "Days overdue", cell: (row) => (row.days_overdue ? row.days_overdue : "—"), exportValue: (row) => row.days_overdue, align: "right" },
  { header: "Overdue by", cell: (row) => <StatusDot {...overdueBy(row)} />, exportValue: (row) => overdueBy(row).label },
  { header: "Balance (UGX)", cell: (row) => <span className="font-semibold text-gray-900">{money(row.balance)}</span>, exportValue: (row) => Number(row.balance), align: "right" },
];

function Ageing({ asOf, canExport, onOpenStudent, onOpenSupplier }: { asOf: string; canExport: boolean; onOpenStudent: (student: StudentOption) => void; onOpenSupplier: (supplier: Party) => void }) {
  const receivables = useQuery({ queryKey: ["receivables-ageing", asOf], queryFn: () => financialService.getReceivablesAgeing({ as_of: asOf }) });
  const payables = useQuery({ queryKey: ["payables-ageing", asOf], queryFn: () => financialService.getPayablesAgeing({ as_of: asOf }) });
  const suppliers = useQuery({ queryKey: ["financial-suppliers"], queryFn: financialService.getSuppliers });
  const params = { as_of: asOf };

  return (
    <div className="space-y-4">
      <AgeingSummary
        title="Receivables ageing"
        description={`Unpaid fee invoices as at ${day(asOf)}, by how long each is overdue (UGX).`}
        data={receivables.data}
        loading={receivables.isLoading}
        aside={<ServerExport show={canExport} what="the receivables ageing report" onExport={(format) => financialService.downloadReceivablesAgeing(format, params)} />}
      />
      <ClientRegister
        title="Unpaid fee invoices"
        description="Filter by how overdue, class or due date. Click a row to open the learner's account."
        rows={receivables.data?.detail}
        config={receivablesAgeingConfig}
        columns={ageingColumns("Student", "Invoice")}
        rowKey={(row) => row.invoice_id}
        onOpen={(row) => onOpenStudent({ id: row.student_id, full_name: row.student_name, student_id: row.student_code ?? "", class_name: row.class_name })}
        openLabel={() => "Account"}
        noun={["invoice", "invoices"]}
        exportName={{ file: `receivables-ageing-${asOf}`, title: `Receivables ageing as at ${asOf}` }}
        isLoading={receivables.isLoading}
        isError={receivables.isError}
        onRetry={() => void receivables.refetch()}
        emptyText="No fee invoice is unpaid."
        canExport={canExport}
      />

      <AgeingSummary
        title="Payables ageing"
        description={`Approved supplier bills unpaid as at ${day(asOf)}, by how long each is overdue (UGX).`}
        data={payables.data}
        loading={payables.isLoading}
        aside={<ServerExport show={canExport} what="the payables ageing report" onExport={(format) => financialService.downloadPayablesAgeing(format, params)} />}
      />
      <ClientRegister
        title="Unpaid supplier bills"
        description="Click a row to open the supplier's statement."
        rows={payables.data?.detail}
        config={payablesAgeingConfig}
        columns={ageingColumns("Supplier", "Bill")}
        rowKey={(row) => row.invoice_id}
        onOpen={(row) => onOpenSupplier({ id: row.student_id, name: row.student_name })}
        openLabel={() => "Statement"}
        noun={["bill", "bills"]}
        exportName={{ file: `payables-ageing-${asOf}`, title: `Payables ageing as at ${asOf}` }}
        isLoading={payables.isLoading}
        isError={payables.isError}
        onRetry={() => void payables.refetch()}
        emptyText="No approved supplier bill is unpaid."
        canExport={canExport}
      />

      <ReportCard title="Supplier statement" description="Every bill and payment with one supplier, with the running balance, as at the date above.">
        <Field label="Supplier" className="max-w-md">
          <select
            className="input mt-1"
            value=""
            onChange={(event) => {
              const supplier = suppliers.data?.find((entry) => String(entry.id) === event.target.value);
              if (supplier) onOpenSupplier({ id: supplier.id, name: supplier.name });
            }}
          >
            <option value="">Choose a supplier…</option>
            {suppliers.data?.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}
          </select>
        </Field>
      </ReportCard>
    </div>
  );
}

// Reconciliations --------------------------------------------------------------------

interface ProofFailure {
  what: string;
  error: unknown;
  retry: () => void;
}

const statusOf = (error: unknown) => (error as { response?: { status?: number } })?.response?.status;

/** The role the server names in “…”, or its whole message if it names none. */
const missingRole = (error: unknown) => {
  const message = apiErrorMessage(error, "");
  return message.match(/“([^”]+)”/)?.[1] ?? message;
};

/**
 * Why the ledger proofs could not be drawn up, in one notice. A 400 means an
 * account role has no account yet; that is fixed under Account roles, not by
 * trying again, so those are listed together with one link.
 */
function ProofErrors({ failures }: { failures: ProofFailure[] }) {
  const unset = failures.filter((failure) => statusOf(failure.error) === 400);
  const broken = failures.filter((failure) => statusOf(failure.error) !== 400);
  if (!failures.length) return null;
  return (
    <div role="alert" className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="space-y-3">
        {unset.length > 0 && (
          <div className="space-y-2">
            <p className="font-medium">
              {unset.length === 1 ? "An account role needs" : "Two account roles need"} an account before {unset.length === 1 ? "this proof" : "these proofs"} can run
            </p>
            <ul className="list-disc space-y-0.5 pl-5">
              {unset.map((failure) => (
                <li key={failure.what}>
                  <span className="font-medium">{failure.what}</span> needs an account for “{missingRole(failure.error)}”.
                </li>
              ))}
            </ul>
            <p>These are the ledger accounts the records are checked against. Once each is assigned and approved, the proof appears here.</p>
            <Link to="/financial/accounts?view=roles" className="btn btn-outline inline-flex bg-white py-1.5 text-sm">
              Open Account roles
            </Link>
          </div>
        )}
        {broken.map((failure) => (
          <p key={failure.what}>
            <span className="font-medium">The {failure.what.toLowerCase()} could not be loaded.</span> {apiErrorMessage(failure.error, "The server could not prepare it.")}{" "}
            <button type="button" onClick={failure.retry} className="font-medium text-primary-700 underline">Try again</button>
          </p>
        ))}
      </div>
    </div>
  );
}

function ProofCard({
  title,
  description,
  reconciled,
  checking,
  subsidiaryLabel,
  subsidiary,
  controlLabel,
  control,
  difference,
  aside,
  listLabel,
  children,
}: {
  title: string;
  description: string;
  reconciled: boolean;
  checking: boolean;
  subsidiaryLabel: string;
  subsidiary: string;
  controlLabel: string;
  control: string;
  difference: string;
  aside: ReactNode;
  listLabel: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div className="space-y-3">
      <ReportCard
        title={title}
        description={description}
        tone={reconciled ? undefined : "danger"}
        aside={
          <>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm font-medium ${reconciled ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
              {reconciled ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              {checking ? "Checking…" : reconciled ? "Reconciled" : "Difference found"}
            </span>
            {aside}
          </>
        }
      >
        <dl className="grid gap-3 sm:grid-cols-3">
          <Tile label={subsidiaryLabel} value={ugx(subsidiary)} />
          <Tile label={controlLabel} value={ugx(control)} />
          <Tile label="Difference" value={ugx(difference)} tone={reconciled ? "text-green-700" : "text-red-700"} />
        </dl>
        <button type="button" onClick={() => setOpen((value) => !value)} aria-expanded={open} className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-primary-700 hover:underline">
          {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          {open ? "Hide" : "Show"} {listLabel}
        </button>
      </ReportCard>
      {open && children}
    </div>
  );
}

const controlStudentColumns: RegisterColumn<ControlStudent>[] = [
  { header: "Student", cell: (row) => <span className="font-medium text-gray-900">{row.student_name}</span>, exportValue: (row) => row.student_name },
  { header: "Charged", cell: (row) => money(row.charges), exportValue: (row) => Number(row.charges), align: "right" },
  { header: "Cancelled", cell: (row) => money(row.cancellations), exportValue: (row) => Number(row.cancellations), align: "right" },
  { header: "Paid", cell: (row) => money(row.payments), exportValue: (row) => Number(row.payments), align: "right" },
  { header: "Refunded", cell: (row) => money(row.refunds), exportValue: (row) => Number(row.refunds), align: "right" },
  { header: "Credited", cell: (row) => money(row.credits), exportValue: (row) => Number(row.credits), align: "right" },
  { header: "Balance (UGX)", cell: (row) => <span className="font-semibold text-gray-900">{money(row.balance)}</span>, exportValue: (row) => Number(row.balance), align: "right" },
];

const controlSupplierColumns: RegisterColumn<ControlSupplier>[] = [
  { header: "Supplier", cell: (row) => <span className="font-medium text-gray-900">{row.supplier_name}</span>, exportValue: (row) => row.supplier_name },
  { header: "Bills", cell: (row) => row.bill_count, exportValue: (row) => row.bill_count, align: "right" },
  { header: "Billed", cell: (row) => money(row.bills), exportValue: (row) => Number(row.bills), align: "right" },
  { header: "Payments", cell: (row) => row.payment_count, exportValue: (row) => row.payment_count, align: "right" },
  { header: "Paid", cell: (row) => money(row.payments), exportValue: (row) => Number(row.payments), align: "right" },
  { header: "Balance (UGX)", cell: (row) => <span className="font-semibold text-gray-900">{money(row.balance)}</span>, exportValue: (row) => Number(row.balance), align: "right" },
];

export interface BankSignOffActions {
  onPrepare?: (bankAccountId: number, asAt: string, notes: string) => void;
  onWithdraw?: (id: number, reason: string) => void;
  busy: boolean;
}

function Reconciliations({
  asOf,
  start,
  end,
  canExport,
  onOpenStudent,
  onOpenSupplier,
  bankSignOff,
}: {
  asOf: string;
  start: string;
  end: string;
  canExport: boolean;
  onOpenStudent: (student: StudentOption) => void;
  onOpenSupplier: (supplier: Party) => void;
  bankSignOff: BankSignOffActions;
}) {
  const params = { as_of: asOf };
  const receivables = useQuery({ queryKey: ["receivables-control-reconciliation", asOf], queryFn: () => financialService.getReceivablesControlReconciliation(params) });
  const payables = useQuery({ queryKey: ["payables-control-reconciliation", asOf], queryFn: () => financialService.getPayablesControlReconciliation(params) });
  const banks = useQuery({ queryKey: ["bank-accounts"], queryFn: financialService.getBankAccounts });
  const [bankId, setBankId] = useState("");
  const bankReport = useQuery({
    queryKey: ["bank-reconciliation-report", bankId, start, end],
    queryFn: () => financialService.getBankReconciliationStatement(Number(bankId), { start_date: start, end_date: end }),
    enabled: !!bankId,
  });
  const loading = receivables.isLoading || payables.isLoading;

  return (
    <div className="space-y-4">
      {loading && <div className="h-40 animate-pulse rounded-lg bg-gray-100" />}
      <ProofErrors
        failures={[
          ...(receivables.isError ? [{ what: "Student ledger proof", error: receivables.error, retry: () => void receivables.refetch() }] : []),
          ...(payables.isError ? [{ what: "Supplier ledger proof", error: payables.error, retry: () => void payables.refetch() }] : []),
        ]}
      />

      {receivables.data?.control_accounts.map((ca) => (
        <ProofCard
          key={`r-${ca.control_account.id ?? ca.control_account.code}`}
          title={`Student ledger vs receivables control (${ca.control_account.code})`}
          description={`Every student invoice, cancellation, receipt, refund and credit routed to ${ca.control_account.name}, as at ${day(asOf)}, set against the general ledger.`}
          reconciled={ca.is_reconciled}
          checking={receivables.isFetching}
          subsidiaryLabel="Student accounts"
          subsidiary={ca.subsidiary_ledger_total}
          controlLabel={`Ledger account ${ca.control_account.code}`}
          control={ca.control_account_balance}
          difference={ca.difference}
          listLabel={`the ${ca.student_count} student ${ca.student_count === 1 ? "balance" : "balances"} behind it`}
          aside={<ServerExport show={canExport} what="the receivables reconciliation" onExport={(format) => financialService.downloadReceivablesControlReconciliation(format, params)} />}
        >
          <ClientRegister
            title={`Student balances in ${ca.control_account.code}`}
            description="Click a learner to open their account."
            rows={ca.students}
            config={controlStudentConfig}
            columns={controlStudentColumns}
            rowKey={(row) => row.student_id}
            onOpen={(row) => onOpenStudent({ id: row.student_id, full_name: row.student_name, student_id: "" })}
            openLabel={() => "Account"}
            noun={["student", "students"]}
            exportName={{ file: `student-balances-${ca.control_account.code}-${asOf}`, title: `Student balances in ${ca.control_account.code} as at ${asOf}` }}
            emptyText="No student activity as at this date."
            canExport={canExport}
          />
        </ProofCard>
      ))}

      {payables.data?.control_accounts.map((ca) => (
        <ProofCard
          key={`p-${ca.control_account.id ?? ca.control_account.code}`}
          title={`Supplier ledger vs payables control (${ca.control_account.code})`}
          description={`Approved supplier bills and dated payments routed to ${ca.control_account.name}, as at ${day(asOf)}, set against the general ledger.`}
          reconciled={ca.is_reconciled}
          checking={payables.isFetching}
          subsidiaryLabel="Supplier accounts"
          subsidiary={ca.subsidiary_ledger_total}
          controlLabel={`Ledger account ${ca.control_account.code}`}
          control={ca.control_account_balance}
          difference={ca.difference}
          listLabel={`the ${ca.supplier_count} supplier ${ca.supplier_count === 1 ? "balance" : "balances"} behind it`}
          aside={<ServerExport show={canExport} what="the payables reconciliation" onExport={(format) => financialService.downloadPayablesControlReconciliation(format, params)} />}
        >
          <ClientRegister
            title={`Supplier balances in ${ca.control_account.code}`}
            description="Click a supplier to open their statement."
            rows={ca.suppliers}
            config={controlSupplierConfig}
            columns={controlSupplierColumns}
            rowKey={(row) => row.supplier_id}
            onOpen={(row) => onOpenSupplier({ id: row.supplier_id, name: row.supplier_name })}
            openLabel={() => "Statement"}
            noun={["supplier", "suppliers"]}
            exportName={{ file: `supplier-balances-${ca.control_account.code}-${asOf}`, title: `Supplier balances in ${ca.control_account.code} as at ${asOf}` }}
            emptyText="No approved supplier activity as at this date."
            canExport={canExport}
          />
        </ProofCard>
      ))}

      <ReportCard
        title="Bank reconciliation statement"
        description={`The bank's balance and the books' balance for ${range(start, end)}, each adjusted for what the other has not seen yet. Items still waiting to clear are listed however old they are.`}
        aside={bankId ? <ServerExport show={canExport} what="the bank reconciliation statement" onExport={(format) => financialService.downloadBankReconciliationStatement(Number(bankId), format, { start_date: start, end_date: end })} /> : undefined}
      >
        <Field label="Bank account" className="max-w-md">
          <select className="input mt-1" value={bankId} onChange={(event) => setBankId(event.target.value)}>
            <option value="">Choose a bank account…</option>
            {banks.data?.map((bank) => <option key={bank.id} value={bank.id}>{bank.name} · {bank.bank_name}</option>)}
          </select>
        </Field>
        {bankId && bankReport.isLoading && <p className="mt-3 text-sm text-gray-400">Loading the reconciliation…</p>}
        {bankReport.isError && <p className="mt-3 text-sm text-red-700">The reconciliation could not be loaded.</p>}
        {bankReport.data && (
          <BankReconciliationStatementView
            data={bankReport.data}
            busy={bankSignOff.busy}
            onPrepare={bankSignOff.onPrepare && ((notes) => bankSignOff.onPrepare!(Number(bankId), bankReport.data.end_date, notes))}
            onWithdraw={bankSignOff.onWithdraw}
          />
        )}
      </ReportCard>
    </div>
  );
}

// Grants and assets ------------------------------------------------------------------

const grantColumns: RegisterColumn<GrantSummaryRow>[] = [
  { header: "Grant", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name, className: "max-w-[14rem] truncate" },
  { header: "Donor", cell: (row) => row.donor, exportValue: (row) => row.donor, className: "max-w-[12rem] truncate text-gray-700" },
  { header: "Restriction", cell: (row) => humanise(row.restriction), exportValue: (row) => humanise(row.restriction), className: "text-gray-600" },
  { header: "Funding period", cell: (row) => range(row.funding_period_start, row.funding_period_end), exportValue: (row) => `${row.funding_period_start} to ${row.funding_period_end}`, className: "tabular-nums text-gray-600" },
  { header: "Committed", cell: (row) => money(row.total_amount), exportValue: (row) => Number(row.total_amount), align: "right" },
  { header: "Received", cell: (row) => money(row.received_amount), exportValue: (row) => Number(row.received_amount), align: "right" },
  { header: "Spent", cell: (row) => money(row.spent_amount), exportValue: (row) => Number(row.spent_amount), align: "right" },
  { header: "Balance (UGX)", cell: (row) => <span className="font-semibold text-gray-900">{money(row.balance)}</span>, exportValue: (row) => Number(row.balance), align: "right" },
  { header: "Status", cell: (row) => <StatusDot label={humanise(row.status)} dot={row.status === "active" ? "bg-emerald-500" : "bg-gray-400"} />, exportValue: (row) => humanise(row.status) },
];

const assetColumns: RegisterColumn<FixedAssetRow>[] = [
  { header: "Tag", cell: (row) => row.asset_tag, exportValue: (row) => row.asset_tag, className: "tabular-nums text-gray-600" },
  { header: "Asset", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name, className: "max-w-[16rem] truncate" },
  { header: "Category", cell: (row) => row.category, exportValue: (row) => row.category },
  { header: "Bought", cell: (row) => day(row.purchase_date), exportValue: (row) => row.purchase_date, className: "tabular-nums" },
  { header: "Cost", cell: (row) => money(row.cost), exportValue: (row) => Number(row.cost), align: "right" },
  { header: "Depreciation", cell: (row) => money(row.accumulated_depreciation), exportValue: (row) => Number(row.accumulated_depreciation), align: "right" },
  { header: "Book value (UGX)", cell: (row) => <span className="font-semibold text-gray-900">{money(row.net_book_value)}</span>, exportValue: (row) => Number(row.net_book_value), align: "right" },
  { header: "Status", cell: (row) => <StatusDot label={humanise(row.status)} dot={assetStatusDot(row.status)} />, exportValue: (row) => humanise(row.status) },
];

function GrantsAndAssets({ canExport, onOpenGrant, onOpenAsset }: { canExport: boolean; onOpenGrant: (grant: GrantSummaryRow) => void; onOpenAsset: (asset: FixedAssetRow) => void }) {
  const grants = useQuery({ queryKey: ["grant-summary-report"], queryFn: financialService.getGrantSummaryReport });
  const assets = useQuery({ queryKey: ["fixed-asset-register-report"], queryFn: financialService.getFixedAssetRegisterReport });
  const unspent = Number(grants.data?.total_received ?? 0) - Number(grants.data?.total_spent ?? 0);

  return (
    <div className="space-y-4">
      <ReportCard
        title="Grants"
        description="Donor money is kept apart from fee income. Balances are what has been received and not yet spent."
        aside={<ServerExport show={canExport} what="the grant summary" onExport={(format) => financialService.downloadGrantSummaryReport(format)} />}
      >
        <dl className="grid grid-cols-2 gap-2 sm:grid-cols-5">
          <Tile label="Grants" value={grants.data?.total_grants ?? "—"} />
          <Tile label="Committed (UGX)" value={money(grants.data?.total_committed)} />
          <Tile label="Received (UGX)" value={money(grants.data?.total_received)} />
          <Tile label="Spent (UGX)" value={money(grants.data?.total_spent)} />
          <Tile label="Unspent (UGX)" value={money(unspent)} tone="text-primary-700" />
        </dl>
      </ReportCard>
      <ClientRegister
        title="Grant balances"
        description="Click a grant for its statement over the period above."
        rows={grants.data?.detail}
        config={grantSummaryConfig}
        columns={grantColumns}
        rowKey={(row) => row.id}
        onOpen={onOpenGrant}
        openLabel={() => "Statement"}
        noun={["grant", "grants"]}
        exportName={{ file: "grant-balances", title: "Grant balances" }}
        isLoading={grants.isLoading}
        isError={grants.isError}
        onRetry={() => void grants.refetch()}
        emptyText="No grants recorded."
        canExport={canExport}
      />

      <ReportCard
        title="Fixed asset register"
        description="Capitalised assets at cost, less the depreciation charged so far."
        aside={<ServerExport show={canExport} what="the fixed asset register" onExport={(format) => financialService.downloadFixedAssetRegisterReport(format)} />}
      >
        <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Tile label="Assets" value={assets.data?.total_assets ?? "—"} />
          <Tile label="Cost (UGX)" value={money(assets.data?.total_cost)} />
          <Tile label="Depreciation (UGX)" value={money(assets.data?.total_accumulated_depreciation)} />
          <Tile label="Book value (UGX)" value={money(assets.data?.total_net_book_value)} tone="text-primary-700" />
        </dl>
      </ReportCard>
      <ClientRegister
        title="Capitalised assets"
        description="Click an asset for its details."
        rows={assets.data?.detail}
        config={fixedAssetConfig}
        columns={assetColumns}
        rowKey={(row) => row.id}
        onOpen={onOpenAsset}
        noun={["asset", "assets"]}
        exportName={{ file: "fixed-asset-register", title: "Fixed asset register" }}
        isLoading={assets.isLoading}
        isError={assets.isError}
        onRetry={() => void assets.refetch()}
        emptyText="No capitalised assets yet."
        canExport={canExport}
      />
    </div>
  );
}

// Audit trail ------------------------------------------------------------------------

export const auditColumns: RegisterColumn<AuditApprovalEvent>[] = [
  { header: "When", cell: (event) => new Date(event.created_at).toLocaleString(), exportValue: (event) => new Date(event.created_at).toLocaleString(), className: "tabular-nums text-gray-600" },
  { header: "Document", cell: (event) => documentName(event.document_type), exportValue: (event) => documentName(event.document_type) },
  { header: "No.", cell: (event) => `#${event.object_id}`, exportValue: (event) => event.object_id, className: "tabular-nums text-gray-600" },
  { header: "Action", cell: (event) => <span className="font-medium text-gray-900">{event.action_display}</span>, exportValue: (event) => event.action_display },
  { header: "By", cell: (event) => event.actor_name, exportValue: (event) => event.actor_name },
  { header: "Status change", cell: (event) => (event.from_status || event.to_status ? `${humanise(event.from_status)} → ${humanise(event.to_status)}` : "—"), exportValue: (event) => `${event.from_status} → ${event.to_status}`, className: "text-gray-600" },
  { header: "Comment", cell: (event) => event.comment || "—", exportValue: (event) => event.comment, className: "max-w-[18rem] truncate text-gray-600" },
];

function AuditTrail({ start, end, canExport, onOpenEvent }: { start: string; end: string; canExport: boolean; onOpenEvent: (event: AuditApprovalEvent) => void }) {
  const events = useQuery({
    queryKey: ["audit-approval-report", start, end],
    queryFn: () => financialService.getAuditApprovalReport({ start_date: start, end_date: end }),
  });
  const count = events.data?.events.length ?? 0;
  return (
    <ClientRegister
      title="Audit & approval trail"
      description={`${range(start, end)}${count >= 1000 ? " · showing the latest 1,000 actions; narrow the period to see earlier ones" : ""}. Click a row for the full comment.`}
      rows={events.data?.events}
      config={auditEventConfig}
      columns={auditColumns}
      rowKey={(event) => event.id}
      onOpen={onOpenEvent}
      noun={["action", "actions"]}
      exportName={{ file: `audit-trail-${start}-to-${end}`, title: `Audit & approval trail, ${start} to ${end}` }}
      isLoading={events.isLoading}
      isError={events.isError}
      onRetry={() => void events.refetch()}
      emptyText="No workflow activity in this period."
      canExport={canExport}
    />
  );
}

// Statements opened from a row -------------------------------------------------------

export function SupplierStatementModal({ supplier, asOf, canExport, onClose }: { supplier: Party; asOf: string; canExport: boolean; onClose: () => void }) {
  const statement = useQuery({
    queryKey: ["supplier-statement-report", String(supplier.id), asOf],
    queryFn: () => financialService.getSupplierStatement(supplier.id, { as_of: asOf }),
  });
  return (
    <Modal
      isOpen
      size="4xl"
      title={`Supplier statement · ${supplier.name}`}
      onClose={onClose}
      footer={
        <>
          <ServerExport show={canExport} what="this statement" onExport={(format) => financialService.downloadSupplierStatement(supplier.id, format, { as_of: asOf })} />
          <button type="button" onClick={onClose} className="btn-outline">Close</button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg bg-primary-50 px-4 py-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-primary-700">Owed to {supplier.name}</p>
            <p className="text-2xl font-semibold tabular-nums text-gray-900">{statement.data ? ugx(statement.data.outstanding_balance) : "…"}</p>
          </div>
          <p className="text-sm text-gray-600">As at {day(asOf)}</p>
        </div>
        {statement.isError ? (
          <p className="text-sm text-red-700">The statement could not be loaded.</p>
        ) : (
          <SheetTable
            rows={statement.data?.events}
            rowKey={(event) => `${event.date}-${event.type}-${event.reference}-${event.balance}`}
            empty="No bills or payments with this supplier as at this date."
            columns={[
              { header: "Date", cell: (event) => day(event.date) },
              { header: "What", cell: (event) => humanise(event.type) },
              { header: "Reference", cell: (event) => event.reference || "—" },
              { header: "Description", cell: (event) => <span className="whitespace-normal">{event.description || "—"}</span> },
              { header: "Billed", cell: (event) => (Number(event.debit) ? money(event.debit) : ""), align: "right" },
              { header: "Paid", cell: (event) => (Number(event.credit) ? money(event.credit) : ""), align: "right" },
              { header: "Balance", cell: (event) => money(event.balance), align: "right" },
            ]}
          />
        )}
      </div>
    </Modal>
  );
}

function GrantStatementModal({ grant, start, end, canExport, onClose }: { grant: GrantSummaryRow; start: string; end: string; canExport: boolean; onClose: () => void }) {
  const period = { start_date: start, end_date: end };
  const statement = useQuery({
    queryKey: ["grant-statement-report", String(grant.id), start, end],
    queryFn: () => financialService.getGrantStatementReport(grant.id, period),
  });
  return (
    <Modal
      isOpen
      size="4xl"
      title={`Grant statement · ${grant.name}`}
      onClose={onClose}
      footer={
        <>
          <ServerExport show={canExport} what="this statement" onExport={(format) => financialService.downloadGrantStatementReport(grant.id, format, period)} />
          <button type="button" onClick={onClose} className="btn-outline">Close</button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap items-end justify-between gap-3 rounded-lg bg-primary-50 px-4 py-3">
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-primary-700">Closing balance</p>
            <p className="text-2xl font-semibold tabular-nums text-gray-900">{statement.data ? ugx(statement.data.closing_balance) : "…"}</p>
            <p className="mt-0.5 text-sm text-gray-600">{grant.donor} · {humanise(grant.restriction)}</p>
          </div>
          <p className="text-sm text-gray-600">{range(start, end)}</p>
        </div>
        {statement.isError ? (
          <p className="text-sm text-red-700">The statement could not be loaded.</p>
        ) : (
          <SheetTable
            rows={statement.data?.events}
            rowKey={(event) => `${event.date}-${event.type}-${event.balance}-${event.description}`}
            empty="No money in or out of this grant in the period."
            columns={[
              { header: "Date", cell: (event) => day(event.date) },
              { header: "What", cell: (event) => humanise(event.type) },
              { header: "Description", cell: (event) => <span className="whitespace-normal">{event.description || "—"}</span> },
              { header: "Account", cell: (event) => event.account || "—" },
              { header: "Received", cell: (event) => (Number(event.receipt) ? money(event.receipt) : ""), align: "right" },
              { header: "Spent", cell: (event) => (Number(event.expense) ? money(event.expense) : ""), align: "right" },
              { header: "Balance", cell: (event) => money(event.balance), align: "right" },
            ]}
          />
        )}
      </div>
    </Modal>
  );
}

// The page ---------------------------------------------------------------------------

export default function ManagementReportsView({
  reportStart,
  reportEnd,
  asOfDate,
  onPeriodChange,
  onAsOfChange,
  canExport,
  canViewAuditLogs,
  onOpenStudent,
  bankSignOff,
}: {
  reportStart: string;
  reportEnd: string;
  asOfDate: string;
  onPeriodChange: (start: string, end: string) => void;
  onAsOfChange: (day: string) => void;
  canExport: boolean;
  canViewAuditLogs: boolean;
  onOpenStudent: (student: StudentOption) => void;
  bankSignOff: BankSignOffActions;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const views = REPORT_VIEWS.filter((entry) => entry.key !== "audit" || canViewAuditLogs);
  const requested = searchParams.get("view");
  const view: ReportView = isReportView(requested) && views.some((entry) => entry.key === requested) ? requested : "income";
  const showView = (next: ReportView) => {
    const params = new URLSearchParams(searchParams);
    params.set("view", next);
    setSearchParams(params, { replace: true });
  };

  const today = todayIso();
  const terms = useQuery({ queryKey: ["financial-academic-terms"], queryFn: academicSetupService.getTerms });
  const periods = quickPeriods(today, terms.data ?? []);
  const days = quickDays(today, terms.data ?? []);

  const [supplier, setSupplier] = useState<Party | null>(null);
  const [grant, setGrant] = useState<GrantSummaryRow | null>(null);
  const [asset, setAsset] = useState<FixedAssetRow | null>(null);
  const [auditEvent, setAuditEvent] = useState<AuditApprovalEvent | null>(null);

  const period = (hint: string) => <PeriodBar start={reportStart} end={reportEnd} onChange={onPeriodChange} periods={periods} hint={hint} />;
  const asAt = (hint: string) => <AsAtBar value={asOfDate} onChange={onAsOfChange} days={days} hint={hint} />;
  const dateBar: Record<ReportView, ReactNode> = {
    income: period("The income statement, fee collection and cash flow cover this period. The balance sheet is cumulative to its last day."),
    arrears: <p className="text-sm text-gray-600">Arrears are shown as they stand today; the ageing section shows them on any past date.</p>,
    ageing: asAt("Invoices and bills are aged by how far past their due date they were on this day."),
    reconciliations: (
      <div className="space-y-4">
        {asAt("The student and supplier ledgers are proved against the general ledger on this day.")}
        {period("The bank reconciliation lists matched lines in this period, and runs to its last day.")}
      </div>
    ),
    grants: period("Grant statements cover this period. Balances and the asset register are as they stand today."),
    audit: period("Actions taken between these two days, inclusive."),
  };

  return (
    <div className="space-y-4">
      <div className="card">
        <div className="card-titlebar">
          <div>
            <h2 className="font-semibold text-gray-900">Management reports</h2>
            <p className="mt-1 text-sm text-gray-500">{VIEW_HINTS[view]}</p>
          </div>
        </div>
        <div className="space-y-4 px-6 py-4">
          <div role="group" aria-label="Report section" className="segmented">
            {views.map((entry) => (
              <button key={entry.key} type="button" className="segmented-option px-4 py-1.5 text-sm" aria-pressed={view === entry.key} onClick={() => showView(entry.key)}>
                {entry.label}
              </button>
            ))}
          </div>
          <div className="filter-panel">{dateBar[view]}</div>
        </div>
      </div>

      {view === "income" && <IncomeAndCash start={reportStart} end={reportEnd} canExport={canExport} />}
      {view === "arrears" && <Arrears canExport={canExport} onOpenStudent={onOpenStudent} />}
      {view === "ageing" && <Ageing asOf={asOfDate} canExport={canExport} onOpenStudent={onOpenStudent} onOpenSupplier={setSupplier} />}
      {view === "reconciliations" && (
        <Reconciliations asOf={asOfDate} start={reportStart} end={reportEnd} canExport={canExport} onOpenStudent={onOpenStudent} onOpenSupplier={setSupplier} bankSignOff={bankSignOff} />
      )}
      {view === "grants" && <GrantsAndAssets canExport={canExport} onOpenGrant={setGrant} onOpenAsset={setAsset} />}
      {view === "audit" && canViewAuditLogs && <AuditTrail start={reportStart} end={reportEnd} canExport={canExport} onOpenEvent={setAuditEvent} />}

      {supplier && <SupplierStatementModal supplier={supplier} asOf={asOfDate} canExport={canExport} onClose={() => setSupplier(null)} />}
      {grant && <GrantStatementModal grant={grant} start={reportStart} end={reportEnd} canExport={canExport} onClose={() => setGrant(null)} />}
      {asset && (
        <DetailsModal
          title={`${asset.asset_tag} · ${asset.name}`}
          headline={{ label: "Book value", value: ugx(asset.net_book_value), note: `Cost ${ugx(asset.cost)}, less ${ugx(asset.accumulated_depreciation)} depreciation` }}
          status={{ label: humanise(asset.status), dot: assetStatusDot(asset.status) }}
          facts={[
            ["Asset tag", asset.asset_tag],
            ["Category", asset.category],
            ["Supplier", asset.supplier],
            ["Bought", day(asset.purchase_date)],
            ["Cost", ugx(asset.cost)],
            ["Depreciation to date", ugx(asset.accumulated_depreciation)],
            ["Disposed of", asset.disposal_date ? day(asset.disposal_date) : ""],
          ]}
          onClose={() => setAsset(null)}
        />
      )}
      {auditEvent && (
        <DetailsModal
          title={`${documentName(auditEvent.document_type)} #${auditEvent.object_id}`}
          headline={{ label: "Action", value: auditEvent.action_display, note: new Date(auditEvent.created_at).toLocaleString() }}
          facts={[
            ["Document", documentName(auditEvent.document_type)],
            ["Number", `#${auditEvent.object_id}`],
            ["By", auditEvent.actor_name],
            ["From status", auditEvent.from_status ? humanise(auditEvent.from_status) : ""],
            ["To status", auditEvent.to_status ? humanise(auditEvent.to_status) : ""],
          ]}
          sections={[{ title: "Comment", body: auditEvent.comment }]}
          onClose={() => setAuditEvent(null)}
        />
      )}
    </div>
  );
}
